import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  IStackTokens,
  IColumn,
  Stack,
  ShimmeredDetailsList,
  Text,
} from '@fluentui/react';
import SubjectDetail from '../SubjectDetail/SubjectDetail';
import { SubjectTeacher, Professor } from '../../models';
import { getSubjects } from 'src/endpoints/getSubjects';
import { useEmployee } from '../Contexts/EmployeeContext';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Menu,
  // MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import { useSubject } from '../Contexts/SubjectContext';
import { getProgress } from 'src/endpoints/getProgress';
import { Progress } from 'src/models/progress';

const stackTokens: IStackTokens = {
  childrenGap: 20,
  padding: 20,
};

const baseColumns: IColumn[] = [
  {
    key: 'SubjectCode',
    name: 'Clave Materia',
    fieldName: 'SubjectCode',
    minWidth: 100,
    maxWidth: 150,
  },
  {
    key: 'SubjectName',
    name: 'Nombre Materia',
    fieldName: 'SubjectName',
    minWidth: 300,
    maxWidth: 500,
  },
  {
    key: 'Group',
    name: 'Grupo',
    fieldName: 'Group',
    minWidth: 100,
    maxWidth: 150,
  },
  { key: 'Plan', name: 'Plan', fieldName: 'Plan', minWidth: 50, maxWidth: 100 },
];

// Shared styles for progress cells
const baseCellStyle: React.CSSProperties = {
  color: 'white',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const submittedStyle: React.CSSProperties = {
  ...baseCellStyle,
  width: 20,
  height: 20,
  backgroundColor: '#9fd89f',
};
const missingStyle: React.CSSProperties = {
  ...baseCellStyle,
  width: 20,
  height: 20,
  backgroundColor: '#f1bbbc',
};
const blockGradeChange = Date.now() > new Date('2026-07-03').getTime();

// Custom hook to fetch professor and progress data
function useProfessorData(employeeId: string) {
  const [professor, setProfessor] = useState<Professor | undefined>();
  const [progresses, setProgresses] = useState<Progress[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const formattedId = employeeId.padStart(6, '0');
        const [subjectsRes, progressRes] = await Promise.all([
          getSubjects(formattedId),
          getProgress(employeeId),
        ]);

        if (isMounted) {
          if (subjectsRes.data) {
            setProfessor(subjectsRes.data);
          } else {
            console.warn('No se encontraron materias para el profesor');
          }

          if (progressRes.data?.Progresses) {
            setProgresses(progressRes.data.Progresses);
          } else {
            console.warn('No se encontraron avances para el profesor');
          }
        }
      } catch (err: any) {
        if (isMounted) {
          console.error(err);
          setError('Error al cargar datos del profesor');
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [employeeId]);

  return { professor, progresses, isLoading, error };
}

interface ActionsMenuProps {
  item: SubjectTeacher;
  onChangeGrade: (item: SubjectTeacher) => void;
  // onUploadFile: (item: SubjectTeacher) => void;
  // onUploadEvaluationForm: (item: SubjectTeacher) => void;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  item,
  onChangeGrade,
  // onUploadFile,
  // onUploadEvaluationForm,
}) => (
  <Menu>
    <MenuTrigger disableButtonEnhancement>
      <Button appearance="primary" aria-label="Menu">
        Menu
      </Button>
    </MenuTrigger>
    <MenuPopover>
      <MenuList>
        <MenuGroup>
          <MenuGroupHeader>Estudiantes</MenuGroupHeader>
          <MenuItem
            disabled={blockGradeChange}
            onClick={() => onChangeGrade(item)}
          >
            Cambiar calificación alumno
          </MenuItem>
        </MenuGroup>
        {/* <MenuDivider />
        <MenuGroup>
          <MenuGroupHeader>Materia</MenuGroupHeader>
          <MenuItem onClick={() => onUploadFile(item)}>Subir avances</MenuItem>
          <MenuItem onClick={() => onUploadEvaluationForm(item)}>
            Subir forma de evaluación
          </MenuItem>
        </MenuGroup> */}
      </MenuList>
    </MenuPopover>
  </Menu>
);

const UserDetail: React.FC = () => {
  const [selectedSubject, setSelectedSubject] = useState<SubjectTeacher | null>(
    null
  );
  const { employee } = useEmployee();
  const { setSubject } = useSubject();
  const navigate = useNavigate();

  const { professor, progresses, isLoading, error } = useProfessorData(
    employee.EmployeeId
  );

  useEffect(() => {
    if (selectedSubject) {
      setSubject(selectedSubject);
    }
  }, [selectedSubject, setSubject]);

  const handleChangeGrade = useCallback((item: SubjectTeacher) => {
    setSelectedSubject(item);
  }, []);

  const handleUploadFile = useCallback(
    (item: SubjectTeacher) => {
      setSubject(item);
      navigate(`/upload-file/${item.SubjectCode}`);
    },
    [navigate, setSubject]
  );

  const handleUploadEvaluationForm = useCallback(
    (item: SubjectTeacher) => {
      setSubject(item);
      navigate(`/upload-evaluation-form/${item.SubjectCode}`);
    },
    [navigate, setSubject]
  );

  const renderProgressCell = useCallback(
    (item: SubjectTeacher, progressNumber: number) => {
      const isSubmitted = progresses.some(
        (progress) =>
          progress.SubjectCode === item.SubjectCode &&
          progress.Group === item.Group &&
          progress.ProgressNumber === progressNumber
      );
      const style = isSubmitted ? submittedStyle : missingStyle;
      return <div style={style} />;
    },
    [progresses]
  );

  const columns = useMemo<IColumn[]>(
    () => [
      ...baseColumns,
      {
        key: 'FE',
        name: 'FE',
        minWidth: 25,
        maxWidth: 25,
        onRender: (item: SubjectTeacher) => renderProgressCell(item, 4),
      },
      {
        key: 'A1',
        name: 'A1',
        minWidth: 25,
        maxWidth: 25,
        onRender: (item: SubjectTeacher) => renderProgressCell(item, 1),
      },
      {
        key: 'A2',
        name: 'A2',
        minWidth: 25,
        maxWidth: 25,
        onRender: (item: SubjectTeacher) => renderProgressCell(item, 2),
      },
      {
        key: 'A3',
        name: 'A3',
        minWidth: 25,
        maxWidth: 25,
        onRender: (item: SubjectTeacher) => renderProgressCell(item, 3),
      },
      {
        key: 'actions',
        name: '',
        minWidth: 250,
        maxWidth: 300,
        onRender: (item: SubjectTeacher) => (
          <ActionsMenu
            item={item}
            onChangeGrade={handleChangeGrade}
            // onUploadFile={handleUploadFile}
            // onUploadEvaluationForm={handleUploadEvaluationForm}
          />
        ),
      },
    ],
    [
      handleChangeGrade,
      handleUploadFile,
      handleUploadEvaluationForm,
      renderProgressCell,
    ]
  );

  if (error && !isLoading) {
    return (
      <Text variant="xLarge" style={{ color: 'red' }}>
        {error}
      </Text>
    );
  }

  if (selectedSubject && professor) {
    return (
      <SubjectDetail
        professor={professor}
        subject={selectedSubject}
        academy={selectedSubject.Academy}
        plan={selectedSubject.Plan}
      />
    );
  }

  if (!professor && !isLoading) {
    return (
      <Text variant="xLarge">No hay datos relacionados con el profesor.</Text>
    );
  }

  return (
    <div>
      {professor && (
        <Stack tokens={stackTokens}>
          <Stack horizontal tokens={stackTokens}>
            <Text variant="xLarge">
              Nombre profesor/a: {professor.TeacherName}
            </Text>
            <Text variant="xLarge">
              Número de empleado: {employee.EmployeeId}
            </Text>
            {employee.Role === 2 && (
              <Button
                appearance="primary"
                onClick={() => navigate('/dashboard')}
              >
                Dashboard
              </Button>
            )}
          </Stack>
          <ShimmeredDetailsList
            items={professor.Subjects}
            columns={columns}
            setKey="set"
            layoutMode={1}
            enableShimmer={isLoading}
          />
        </Stack>
      )}
    </div>
  );
};

export default UserDetail;
